import { StatusBar } from 'expo-status-bar';
import { observer } from 'mobx-react-lite';
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { Animated, Easing, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { Text } from '@/components/Text';
import { colors, fontSizes, radii, shadows, spacing } from '@/constants/theme';
import { voltrUploadStore } from '@/stores/voltrUpload.store';
import { bandShowing, percentSent } from '@/stores/voltrUploadTypes';

const SLIDE_MS = 220;
/** The band has to stay a band: uncapped, the largest text size fills the screen with amber. */
const MAX_TEXT_SCALE = 1.3;
/** More than the band's height below the clock, shadow included, at normal text sizes. Larger text can make it taller, so idle it's also see-through. */
const OFF_SCREEN_PAST_CLOCK = 200;

/**
 * Wraps the whole app. While a video sends, an amber band slides down over the
 * top of the screen and fills up as the video goes: the upload only runs while
 * the app is on screen, so this can't be something they scroll past. It floats
 * so the page never moves, and slides on the native driver because hashing the
 * video keeps JS busy from the moment Submit is tapped. Always mounted, waiting
 * off screen, so nothing has to render before it can start moving.
 */
export const UploadStayFrame = observer(function UploadStayFrame({ children }: { children: ReactNode }) {
  const insets = useSafeAreaInsets();
  const store = voltrUploadStore;
  // A Cancel or logout empties the list while its loop unwinds, and once every byte is with Voltr there's no need to stay.
  const showing = bandShowing(store.running, store.items);
  const percent = percentSent(store.items);
  const hiddenAt = -(insets.top + OFF_SCREEN_PAST_CLOCK);
  const slide = useRef(new Animated.Value(showing ? 1 : 0)).current;

  useLayoutEffect(() => {
    Animated.timing(slide, {
      toValue: showing ? 1 : 0,
      duration: SLIDE_MS,
      easing: Easing.out(Easing.cubic),
      useNativeDriver: true,
    }).start();
  }, [showing, slide]);

  const translateY = slide.interpolate({ inputRange: [0, 1], outputRange: [hiddenAt, 0] });
  const opacity = slide.interpolate({ inputRange: [0, 0.05], outputRange: [0, 1], extrapolate: 'clamp' });
  return (
    <View style={styles.frame}>
      {children}
      {/* Mounted after any screen's own, so the clock stays dark on amber even over a photo hero. The deal page yields too (bandShowing), since it remounts its own on coming back. */}
      {showing ? <StatusBar style="dark" /> : null}
      <Animated.View
        accessible={showing}
        aria-hidden={!showing}
        accessibilityRole="alert"
        accessibilityLabel="Stay on this screen until your upload finishes."
        accessibilityValue={{ min: 0, max: 100, now: percent }}
        // Touches pass through: Back sits under it, and moving around the app doesn't stop the upload.
        pointerEvents="none"
        style={[styles.band, { opacity, transform: [{ translateY }] }]}
        testID="upload-stay-band"
      >
        <View style={styles.face}>
          <View style={[styles.fill, { width: `${percent}%` }]} testID="upload-stay-fill" />
          <View style={[styles.row, { paddingTop: insets.top + spacing.sm }]}>
            <View style={styles.copy}>
              <Text variant="bodyBold" style={styles.title} maxFontSizeMultiplier={MAX_TEXT_SCALE}>
                Stay on this screen
              </Text>
              <Text variant="bodyMedium" maxFontSizeMultiplier={MAX_TEXT_SCALE}>
                until your upload finishes.
              </Text>
            </View>
            {/* Nothing moves while the video is being prepared, and a stuck 0% reads as frozen. */}
            {percent > 0 ? (
              <Text variant="bodyBold" style={styles.percent} maxFontSizeMultiplier={MAX_TEXT_SCALE} testID="upload-stay-percent">
                {percent}%
              </Text>
            ) : null}
          </View>
        </View>
      </Animated.View>
    </View>
  );
});

const styles = StyleSheet.create({
  frame: { flex: 1, backgroundColor: colors.background },
  // Shadow out here, clipping on the face: one view can't do both on iOS.
  band: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    borderBottomLeftRadius: radii['2xl'],
    borderBottomRightRadius: radii['2xl'],
    // Full-strength amber even at 0%: hashing a long video can sit there a while, and it still has to pop.
    backgroundColor: colors.warning,
    ...shadows.modal,
  },
  // No padding here: the fill's width is a percent of this box, edge to edge.
  face: {
    overflow: 'hidden',
    borderBottomLeftRadius: radii['2xl'],
    borderBottomRightRadius: radii['2xl'],
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingBottom: spacing.lg,
  },
  fill: { position: 'absolute', left: 0, top: 0, bottom: 0, backgroundColor: colors.warningFill },
  copy: { flex: 1 },
  title: { fontSize: fontSizes['2xl'], lineHeight: 30 },
  percent: { fontSize: fontSizes['3xl'], lineHeight: 36 },
});
